import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import apiWithRefresh from "../utils/apiWithRefresh";
import Api from "../utils/api";
import Payment from "./Payment";
import { Helmet } from "react-helmet-async";
import {
  CheckCircle,
  School,
  Payment as PaymentIcon,
  HourglassEmpty,
  ErrorOutline,
  AssessmentTwoTone,
} from "@mui/icons-material";

import {
  Box,
  Typography,
  Paper,
  Stepper,
  Step,
  StepLabel,
  Alert,
} from "@mui/material";

import assessmentStatusEnum from "../utils/assessmentstatusEnum";
import FeesTypeEnum from "../utils/FeesTypeEnum";
import { handleResendPaymentToken } from "../utils/paymentActions";

const EnrollmentProgress = () => {
  const { id } = useParams();
  const location = useLocation();
  const [criteria, setCriteria] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPayment, setShowPayment] = useState(false);
  const [paymentType, setPaymentType] = useState(null);

  // Extract token from query string if present
  const searchParams = new URLSearchParams(location.search);
  const token = searchParams.get("token");

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    let url = `learner-enrollments/${id}/criteria`;
    if (token) url += `?token=${encodeURIComponent(token)}`;
    const fetchCriteria = async () => {
      try {
        let data;
        if (token) {
          data = await Api.get(url, token);
        } else {
          data = await apiWithRefresh.get(url);
        }
        setCriteria(data);
        setLoading(false);
      } catch (err) {
        handleResendPaymentToken(id);
        setError(err.message || "Failed to fetch enrollment criteria");
        setLoading(false);
      }
    };
    fetchCriteria();
  }, [id, token]);

  // Handler to show payment form for the required fee
  const handlePay = (type) => {
    setPaymentType(type);
    setShowPayment(true);
  };

  // Stepper logic for progress visualization
  const steps = [
    "Admission Fee",
    "Assessment",
    //"Special Chance Fee",
    "Batch Allocation",
    "Monthly Fee",
    "Account Activation",
  ];
  let activeStep = 0;
  if (criteria && criteria.admissionFeePaid) activeStep = 1;
  if (
    criteria &&
    criteria.admissionFeePaid &&
    criteria.assessmentStatus === assessmentStatusEnum.PASS
  )
    activeStep = 2;
  // if (
  //   criteria &&
  //   criteria.assessmentStatus === assessmentStatusEnum.SPECIAL_CHANCE &&
  //   criteria.specialChanceFeePaid
  // )
  //   activeStep = 3;
  if (criteria && criteria.batchAllocated) activeStep = 3;
  if (criteria && criteria.monthlyFeePaid) activeStep = 4;
  if (criteria && criteria.accountActivated) activeStep = 5;
  if (
    !criteria ||
    (!criteria.admissionFeePaid &&
      !criteria.assessmentStatus &&
      !criteria.specialChanceFeePaid &&
      !criteria.batchAllocated &&
      !criteria.accountActivated)
  )
    activeStep = 0;

  // Status icon and color
  const getStatusIcon = () => {
    if (criteria && criteria.accountActivated)
      return <CheckCircle color="success" sx={{ fontSize: 40 }} />;
    if (criteria && criteria.batchAllocated)
      return <School color="primary" sx={{ fontSize: 40 }} />;
    if (criteria && !criteria.admissionFeePaid)
      return <PaymentIcon color="warning" sx={{ fontSize: 40 }} />;
    if (criteria && criteria.assessmentStatus === assessmentStatusEnum.PENDING)
      return <HourglassEmpty color="info" sx={{ fontSize: 40 }} />;
    if (criteria && criteria.assessmentStatus === assessmentStatusEnum.PASS)
      return <AssessmentTwoTone color="info" sx={{ fontSize: 40 }} />;
    // if (
    //   criteria &&
    //   criteria.assessmentStatus === assessmentStatusEnum.SPECIAL_CHANCE &&
    //   !criteria.specialChanceFeePaid
    // )
    //   return <PaymentIcon color="warning" sx={{ fontSize: 40 }} />;
    return <ErrorOutline color="error" sx={{ fontSize: 40 }} />;
  };

  if (loading) return <div className="container py-4">Loading...</div>;
  if (error) return <div className="container py-4 text-danger">{error}</div>;
  // if (!criteria)
  //   return <div className="container py-4">No enrollment found.</div>;

  // Defensive: criteria may be null
  // Payment step
  if (showPayment && paymentType) {
    // Pass paymentType and token as props to Payment
    return (
      <Payment enrollmentId={id} paymentType={paymentType} token={token} />
    );
  }

  // Determine next step and status message
  let statusMsg = "";
  let action = null;
  // Normalize assessmentStatus to lowercase for comparison
  const normalizedAssessmentStatus = (
    criteria.assessmentStatus || ""
  ).toLowerCase();
  if (criteria.accountActivated) {
    statusMsg =
      "Your account is activated! You can now log in and start learning.";
  } else if (!criteria.admissionFeePaid) {
    statusMsg = "Please pay your admission fee to proceed.";
    action = (
      <button
        className="btn btn-primary"
        onClick={() => handlePay(FeesTypeEnum.ADMISSION)}
      >
        Pay Admission Fee
      </button>
    );
  } else if (normalizedAssessmentStatus === assessmentStatusEnum.PENDING) {
    statusMsg = "Your assessment is pending. Please wait for review.";
  } else if (
    normalizedAssessmentStatus === assessmentStatusEnum.PASS &&
    !criteria.monthlyFeePaid &&
    criteria.isCashEnrollment
  ) {
    statusMsg =
      "Congratulations! Your cash enrollment has been approved. Please pay the first monthly fee to proceed.";
    action = (
      <button className="btn btn-primary" onClick={() => handlePay("monthly")}>
        Pay First Month Fee
      </button>
    );
  }
  // else if (
  //   normalizedAssessmentStatus === assessmentStatusEnum.SPECIAL_CHANCE &&
  //   !criteria.specialChanceFeePaid
  // ) {
  //   statusMsg =
  //     "You have been offered a special chance. Please pay the special chance fee.";
  //   action = (
  //     <button
  //       className="btn btn-primary"
  //       onClick={() => handlePay(FeesTypeEnum.SPECIAL_CHANCE)}
  //     >
  //       Pay Special Chance Fee
  //     </button>
  //   );
  // }
  else if (
    normalizedAssessmentStatus === assessmentStatusEnum.PASS &&
    criteria.monthlyFeePaid
  ) {
    statusMsg =
      "Congratulations! Your monthly fee will be auto-debited. No action is required from your side.";
    action = null;
  } else if (criteria.batchAllocated && !criteria.accountActivated) {
    statusMsg = "Batch allocated. Awaiting account activation.";
  } else {
    statusMsg = "Your enrollment is being processed. Please check back soon.";
  }

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS | Enrollment Progress</title>
        <meta
          name="description"
          content="Check your enrollment progress for AKAAL KIDS - Gurmat Education Platform"
        />
      </Helmet>
      <div
        style={{
          width: "100%",
          background: "linear-gradient(90deg, #f6815e 0%, #f6b93b 100%)",
          color: "#fff",
          padding: "38px 0 24px 0",
          textAlign: "center",
          marginBottom: 32,
          boxShadow: "0 4px 24px #f6815e22",
          position: "relative",
          overflow: "hidden",
        }}
      >
        <h1
          style={{
            fontWeight: 900,
            fontSize: "2.6rem",
            letterSpacing: 1,
            marginBottom: 11,
            textShadow: "0 2px 12px #f6815e55",
          }}
        >
          {"Enrollment Progress"}
        </h1>
      </div>
      <Box className="container py-4" maxWidth="md">
        <Paper elevation={3} sx={{ p: 4, borderRadius: 3, mb: 4 }}>
          <Box mb={2} display="flex" justifyContent="center">
            {getStatusIcon()}
          </Box>
          <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 3 }}>
            {steps.map((label, idx) => (
              <Step key={label} completed={activeStep > idx}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Alert
            severity={
              criteria.accountActivated
                ? "success"
                : criteria.admissionFeePaid
                  ? "info"
                  : "warning"
            }
            sx={{ mb: 3, fontSize: 18 }}
          >
            <b>Status:</b> {statusMsg}
          </Alert>
          {action && <Box mb={3}>{action}</Box>}
          <Box mt={4}>
            <Typography variant="h6" gutterBottom>
              Enrollment Details
            </Typography>
            <ul style={{ fontSize: 18, lineHeight: 2 }}>
              <li>
                <b>Name:</b> {criteria.learnerName}
              </li>
              <li>
                <b>Email:</b> {criteria.email}
              </li>
              <li>
                <b>Country:</b> {criteria.country}
              </li>
              <li>
                <b>Assessment Status:</b> {criteria.assessmentStatus}
              </li>
              {criteria.isCashEnrollment && (
                <li>
                  <b>Cash Enrollment:</b> Yes
                </li>
              )}
              {criteria.admissionFeePaid && (
                <li>
                  <b>Admission Fee Paid:</b>{" "}
                  {criteria.admissionFeePaid ? "Yes" : "No"}
                </li>
              )}
              {criteria.monthlyFeePaid && (
                <li>
                  <b>Monthly Fee Paid:</b>{" "}
                  {criteria.monthlyFeePaid ? "Yes" : "No"}
                </li>
              )}
              {/* {criteria.specialChanceFeePaid && (
                <li>
                  <b>Special Chance Fee Paid:</b>{" "}
                  {criteria.specialChanceFeePaid ? "Yes" : "No"}
                </li>
              )} */}
              {criteria.batchAllocated && (
                <li>
                  <b>Batch Allocated:</b>{" "}
                  {criteria.batchAllocated ? "Yes" : "No"}
                </li>
              )}
              {criteria.accountActivated && (
                <li>
                  <b>Account Activated:</b>{" "}
                  {criteria.accountActivated ? "Yes" : "No"}
                </li>
              )}
            </ul>
          </Box>
        </Paper>
      </Box>
    </>
  );
};

export default EnrollmentProgress;
